import type { CrowdexUIMessage, CrowdexUIMessagePart } from './types';

/**
 * Marker error text for a tool call that never finished because the desktop
 * process stopped. The UI renders parts carrying this text as a neutral
 * "Interrupted" state rather than a hard failure, since the operation was
 * never actually run or rejected — it was simply cut off (often while it was
 * still waiting for the user to approve it).
 */
export const INTERRUPTED_ERROR = 'The agent stopped before this operation completed.';

/**
 * A persisted message has no live stream after the desktop process restarts.
 * Settle any active-looking parts so restored history cannot claim they still run.
 */
export function failInterruptedMessages(messages: readonly CrowdexUIMessage[]): CrowdexUIMessage[] {
  return messages.map((message, messageIndex) => ({
    ...message,
    parts: message.parts.flatMap((part) => {
      // A turn that reached a normal finish (or that is followed by another
      // persisted turn) was not interrupted. Some ACP agents omit the final
      // tool_call_update, leaving the last saved tool part looking active even
      // though the prompt itself completed successfully. Restoring that part as
      // a failure changes valid history after an app restart.
      const turnCompleted =
        message.metadata?.stopReason === 'end_turn' || messageIndex < messages.length - 1;
      if (turnCompleted) return [completeStalePart(part)];

      return [failInterruptedPart(part)];
    }),
  }));
}

/** A thought or words cut off mid-stream end where they stopped. */
function endStreamingPart(part: CrowdexUIMessagePart): CrowdexUIMessagePart {
  if ((part.type === 'reasoning' || part.type === 'text') && part.state === 'streaming')
    return { ...part, state: 'done' };
  return part;
}

function failInterruptedPart(part: CrowdexUIMessagePart): CrowdexUIMessagePart {
  if (part.type === 'reasoning' || part.type === 'text') return endStreamingPart(part);
  if (part.type === 'data-elicitation' && part.data.status === 'pending') {
    return {
      ...part,
      data: { ...part.data, status: 'cancelled', outcome: { action: 'cancel' } },
    };
  }
  if (part.type === 'data-subagent') {
    if (part.data.status !== 'pending' && part.data.status !== 'in_progress') return part;
    // Interruption is not a failure: settle to `cancelled` so the card reads as
    // neutral "Interrupted" (and can offer a re-run) rather than a hard error.
    return {
      ...part,
      data: {
        ...part.data,
        status: 'cancelled',
        tools: part.data.tools.map((tool) =>
          tool.status === 'pending' || tool.status === 'in_progress'
            ? { ...tool, status: 'cancelled' }
            : tool,
        ),
      },
    };
  }

  if (
    part.type === 'dynamic-tool' &&
    (part.state === 'input-streaming' || part.state === 'input-available')
  ) {
    return {
      type: 'dynamic-tool',
      toolName: part.toolName,
      toolCallId: part.toolCallId,
      state: 'output-error',
      input: part.input,
      errorText: INTERRUPTED_ERROR,
    };
  }

  return part;
}

function completeStalePart(part: CrowdexUIMessagePart): CrowdexUIMessagePart {
  if (part.type === 'reasoning' || part.type === 'text') return endStreamingPart(part);
  if (part.type === 'data-elicitation' && part.data.status === 'pending') {
    return {
      ...part,
      data: { ...part.data, status: 'cancelled', outcome: { action: 'cancel' } },
    };
  }
  if (part.type === 'data-subagent') {
    if (part.data.status !== 'pending' && part.data.status !== 'in_progress') return part;
    return {
      ...part,
      data: {
        ...part.data,
        status: 'completed',
        tools: part.data.tools.map((tool) =>
          tool.status === 'pending' || tool.status === 'in_progress'
            ? { ...tool, status: 'completed' }
            : tool,
        ),
      },
    };
  }

  if (
    part.type === 'dynamic-tool' &&
    (part.state === 'input-streaming' || part.state === 'input-available')
  ) {
    return {
      type: 'dynamic-tool',
      toolName: part.toolName,
      toolCallId: part.toolCallId,
      ...(part.title === undefined ? {} : { title: part.title }),
      state: 'output-available',
      input: part.input,
      output: null,
    };
  }

  return part;
}
